import React, { useContext, useEffect, useMemo, useState } from "react";

import "./TrendingTodaySection.scss";
import { useTranslation } from "react-i18next";
import TrendContent from "../../TrendContent";
import InputSelection from "../../InputSelection";
import EmptyState from "../../EmptyState";
import GlobalContext from "../../GlobalContext";
import CardCarousel from "../../CardCarousel";

import { base64Encode, getGamesFilterForAPI, getGameTitle, parseDateFromString } from "../../../linemate-react-common/src/util";
import { FILTER_KEYS } from "../../../linemate-react-common/src/filter-config";

function TrendingTodayCarouselCard({ element }) {
  const trend = element.trend;

  return (
    <div className="home-page-list-items-item" onClick={element.onClick}>
      <TrendContent card={trend} league={trend.league || element.league} />
    </div>
  );
}

export function TrendingTodaySection({
  trendingToday,
  maxPreviewTrends,
  league,
}) {
  const context = useContext(GlobalContext);
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(
    Object.keys(trendingToday || {})[0] || ""
  );

  const categoryOptions = useMemo(() => {
    return Object.fromEntries(
      Object.keys(trendingToday || {}).map((key) => [key, key])
    );
  }, [trendingToday]);

  useEffect(() => {
    const optionKeys = Object.keys(categoryOptions);

    if (optionKeys.length === 0) {
      setSelectedCategory("");
      return;
    }

    if (!selectedCategory || !optionKeys.includes(selectedCategory)) {
      setSelectedCategory(optionKeys[0]);
    }
  }, [categoryOptions, selectedCategory]);

  const trends = trendingToday?.[selectedCategory] || [];
  const games =
    context.currentGames && context.currentGames.length > 0
      ? context.currentGames
      : context.upcomingGames || [];

  const buildTrendFeedHref = (trend) => {
    const game = games.find((item) => item.id === trend.gameId)

    if (!game) {
      return `/${league}/trends?tabs=${encodeURIComponent(
        JSON.stringify(["player", "team", "sameGameParlay"])
      )}&tab=${trend.type === "team" ? "team" : "player"}`
    }

    const gameTitleToGameIdDict = Object.fromEntries(
      [game].map((x) => [getGameTitle(league, x), x.id])
    )

    const gameTitleToAnnotationDict = Object.fromEntries(
      [game].map((x) => {
        const parsedDate = parseDateFromString(x.timestamp)
        return [getGameTitle(league, x), `${parsedDate.dayOfWeekShort} ${parsedDate.formattedTime}`]
      })
    )

    const initialFilters = {}
    initialFilters[FILTER_KEYS.GAMES] = getGamesFilterForAPI(
      gameTitleToGameIdDict,
      gameTitleToAnnotationDict
    )
    initialFilters[FILTER_KEYS.GAMES].selectedValues = [game.id]

    const parsedDate = parseDateFromString(game.timestamp)

    const feed = base64Encode(
      JSON.stringify({
        title: `${game.awayTeamData.info.code} @ ${game.homeTeamData.info.code}`,
        annotation: `${parsedDate.formattedDayMonthShort} ${parsedDate.formattedTime}`,
        initialFilters,
        filtersToExclude: [FILTER_KEYS.GAMES],
        selectedTrendId: trend.trendId || trend.id || null,
      })
    )

    return `/${league}/trends?feed=${feed}&tabs=${encodeURIComponent(
      JSON.stringify(["player", "team", "sameGameParlay"])
    )}&tab=${trend.type === "team" ? "team" : "player"}`
  }

  return (
    <div className="trending-today-section-padding">
      {trends.length === 0 && (
        <>
          <div className="trending-today-title-container">
            <div className="trending-today-title-copy">
              <span className="text-style-h-3-semibold color-fig-default">
                {`${t("home.sections.trendingToday")} ${t("web.common.for")}`}
              </span>

              {Object.keys(categoryOptions).length > 0 && (
                <div className="trending-today-dropdown-wrapper">
                  <InputSelection
                    enabled={true}
                    type="single"
                    typography="sm"
                    selection={selectedCategory}
                    options={categoryOptions}
                    selectionHandler={setSelectedCategory}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="trending-today-empty-state-container">
            <EmptyState
              className="trending-today-empty-state"
              text={t("home.empty.trend")}
            />
          </div>
        </>
      )}

      {trends.length > 0 && (
        <CardCarousel
          listOfElements={trends.slice(0, maxPreviewTrends).map((trend) => ({
            trend,
            league,
            onClick: () => {
              window.location.href = buildTrendFeedHref(trend);
            },
          }))}
          elementsPerView={3}
          spaceBetweenSlides={12}
          title=""
          CardComponent={TrendingTodayCarouselCard}
          showHeader={true}
          mobileOnlyControls={true}
          headerContent={
            <div className="trending-today-title-copy">
              <span className="text-style-h-3-semibold color-fig-default">
                {`${t("home.sections.trendingToday")} ${t("web.common.for")}`}
              </span>

              {Object.keys(categoryOptions).length > 0 && (
                <div className="trending-today-dropdown-wrapper">
                  <InputSelection
                    enabled={true}
                    type="single"
                    typography="sm"
                    selection={selectedCategory}
                    options={categoryOptions}
                    selectionHandler={setSelectedCategory}
                  />
                </div>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}